import { authenticatedFetch } from './authApi';
import { getCachedOrFetch } from './requestCache';
import { getEnv } from '../utils/env';

type UnknownRecord = Record<string, unknown>;

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
const DASHBOARD_MATCHES_TTL_MS = 15_000;

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object' && 'error' in (payload as UnknownRecord)) {
    const error = (payload as UnknownRecord).error;
    if (typeof error === 'string' && error.trim().length > 0) return error;
  }
  if (payload && typeof payload === 'object' && 'message' in (payload as UnknownRecord)) {
    const message = (payload as UnknownRecord).message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const dashboardRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}${path}`, options);

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractMessage(payload, `Dashboard API request failed (${response.status})`));
  }

  return extractData(payload);
};

export const getDashboardMatchesApi = async (): Promise<unknown[]> => {
  return await getCachedOrFetch('dashboard:matches', DASHBOARD_MATCHES_TTL_MS, async () => {
    const payload = await dashboardRequest('/api/v1/coach/dashboard/matches', { method: 'GET' });
    return Array.isArray(payload) ? payload : [];
  });
};
